/**
 * HR & payroll (Phase 5): work schedules, holidays, leave, attendance (punches → work days), salary
 * revisions, one-off earnings / deductions, advances & loans, and the monthly payroll run.
 * Gratuity and the WPS salary file are parked (owner decision 2026-10-05).
 */
import { z } from 'zod';
import { always } from './schemas';
import { todayUae } from './masters';

const req = (label: string, max: number) => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const month = z.string({ error: 'Choose the month' }).regex(/^\d{4}-\d{2}$/, 'Choose the month');
const time = z.string({ error: 'Enter the time' }).regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24 h)');
const uuid = (msg: string) => z.string({ error: msg }).uuid(msg);
const money = (label = 'Amount') => z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(1e8, `${label} is too large`));
const positive = (label = 'Amount') => money(label).refine((n) => n > 0, `${label} must be above 0`);

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const WEEKDAY_LABELS: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
export const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

// ---------- Work schedules ----------

export const LATE_MODES = ['PER_MINUTE', 'PER_COUNT'] as const;
export const LATE_MODE_LABELS: Record<(typeof LATE_MODES)[number], string> = { PER_MINUTE: 'By minutes late', PER_COUNT: 'Half a day per number of late days' };
export const breakSchema = z.object({ name: req('Break name', 40), start: time, end: time, paid: z.boolean() });
export type BreakInput = z.infer<typeof breakSchema>;
const spanOf = (b: { start: string; end: string }) => (minutesOf(b.end) - minutesOf(b.start) + 1440) % 1440;
export const breakMinutesOf = (breaks: BreakInput[]) => breaks.filter((b) => !b.paid).reduce((s, b) => s + spanOf(b), 0);

export const workScheduleSchema = z
  .object({
    code: z.string().trim().min(1, 'Code is required').max(20).regex(/^[A-Z0-9_-]+$/, 'Use uppercase letters, numbers, dash'),
    name: req('Name', 80),
    startTime: time,
    endTime: time,
    breakMinutes: z.coerce.number().int().min(0).max(240),
    workDays: z.array(z.number().int().min(1).max(7)).min(1, 'Choose at least one working day'),
    graceMinutes: z.coerce.number({ error: 'Enter minutes' }).int().min(0).max(180).default(0),
    /** A day with fewer worked minutes counts as absent (old system: 2 hours). */
    minimumMinutes: z.coerce.number().int().min(0).max(1440).default(0),
    lateRate: z.coerce.number().min(0).max(10).default(1),
    otRate: z.coerce.number().min(0).max(10).default(1),
    /** Minutes after the shift end before overtime starts counting. */
    otAfterMinutes: z.coerce.number().int().min(0).max(240).default(0),
    earlyArrivalOt: z.boolean().default(false),
    breaks: z.array(breakSchema).max(6).default([]),
    lateMode: z.enum(LATE_MODES).default('PER_MINUTE'),
    /** PER_COUNT: every this many late days = half a day's pay. */
    lateCount: z.coerce.number().int().min(1).max(31).default(3),
    lateFreeTimes: z.coerce.number().int().min(0).max(31).default(0),
    otMinimumMinutes: z.coerce.number().int().min(0).max(480).default(0),
    otMonthlyCapHours: z.coerce.number().int().min(0).max(400).default(0),
    offDayOt: z.boolean().default(false),
    otOffDayRate: z.coerce.number().min(0).max(10).default(1.5),
    isActive: z.boolean(),
  })
  .refine((v) => v.startTime !== v.endTime, { path: ['endTime'], message: 'End must differ from start', ...always })
  // With breaks listed, the unpaid ones are the break minutes.
  .transform((v) => (v.breaks.length ? { ...v, breakMinutes: breakMinutesOf(v.breaks) } : v));
export type WorkScheduleInput = z.infer<typeof workScheduleSchema>;

/** Scheduled working minutes of a day (night shifts cross midnight). */
export function scheduledMinutes(s: { startTime: string; endTime: string; breakMinutes: number }) {
  let m = minutesOf(s.endTime) - minutesOf(s.startTime);
  if (m <= 0) m += 1440;
  return Math.max(m - s.breakMinutes, 1);
}

// ---------- Seasonal shifts (Ramadan, summer …) ----------

export const shiftPeriodSchema = z
  .object({
    name: req('Name', 80),
    fromDate: isoDate,
    toDate: isoDate,
    workScheduleId: uuid('Choose the schedule to use'),
    employeeIds: z.array(z.string().uuid()).max(500).default([]),
    departmentIds: z.array(z.string().uuid()).max(100).default([]),
    baseScheduleIds: z.array(z.string().uuid()).max(100).default([]),
    notes: opt(500),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.toDate >= v.fromDate, { path: ['toDate'], message: 'End date must be on or after the start', ...always })
  .refine((v) => v.toDate <= addMonths(v.fromDate, 12), { path: ['toDate'], message: 'A seasonal shift can run at most a year', ...always });
export type ShiftPeriodInput = z.infer<typeof shiftPeriodSchema>;
function addMonths(d: string, m: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCMonth(x.getUTCMonth() + m);
  return x.toISOString().slice(0, 10);
}

// ---------- Policies ----------

export const POLICY_CATEGORIES = ['Attendance', 'Leave', 'Overtime', 'Conduct', 'Pay & benefits', 'Health & safety', 'Other'] as const;
export const hrPolicySchema = z.object({
  title: req('Title', 120),
  category: z.enum(POLICY_CATEGORIES),
  effectiveFrom: isoDate,
  body: req('Policy text', 20_000),
  isActive: z.boolean().default(true),
});
export type HrPolicyInput = z.infer<typeof hrPolicySchema>;

export const holidaySchema = z.object({ date: isoDate, name: req('Name', 80) });
export type HolidayInput = z.infer<typeof holidaySchema>;

/** Which leave rules a leave type follows. */
export const LEAVE_KINDS = ['ANNUAL', 'SICK', 'MATERNITY', 'PARENTAL', 'COMP_OFF', 'OTHER'] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];
export const LEAVE_KIND_LABELS: Record<LeaveKind, string> = {
  ANNUAL: 'Annual leave (earned, balance, carry forward)',
  SICK: 'Sick leave (full / half / unpaid days a year)',
  MATERNITY: 'Maternity leave (full then half pay)',
  PARENTAL: 'Parental leave (days a year)',
  COMP_OFF: 'Comp-off (days earned by off-day work)',
  OTHER: 'Other (paid or unpaid, optional yearly limit)',
};

export const leaveTypeSchema = z.object({
  code: z.string().trim().min(1).max(20).regex(/^[A-Z0-9_-]+$/, 'Use uppercase letters, numbers, dash'),
  name: req('Name', 80),
  kind: z.enum(LEAVE_KINDS).default('OTHER'),
  paid: z.boolean(),
  /** Days per calendar year; empty = no balance check. */
  annualDays: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(366).nullable()),
  isActive: z.boolean(),
});
export type LeaveTypeInput = z.infer<typeof leaveTypeSchema>;

const days = (label: string, max = 366) => z.coerce.number({ error: `Enter ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(max, `${label} is at most ${max}`);
/** The leave rules (UAE law by default). Kept as the setting `leave.rules`. */
/** The sandwich rule counts from this day (owner: back to 1 October 2026). */
export const SANDWICH_FROM = '2026-10-01';
export const SANDWICH_MODE_LABELS = { OFF: 'Off: weekly offs and holidays are always days off', BEFORE_AND_AFTER: 'Before and after: away on both sides, the off day counts as away too' } as const;

export const leaveRulesSchema = z.object({
  annual: z
    .object({
      daysPerYear: days('Days a year', 60).default(30),
      /** No annual leave is earned in the first months of service. */
      waitMonths: z.coerce.number().int().min(0).max(12).default(6),
      /** Earned per month after the wait, until the first anniversary. */
      firstYearPerMonth: days('Days a month in the first year', 5).default(2),
      carryForward: z.enum(['ALL', 'LIMIT', 'NONE']).default('ALL'),
      carryForwardMax: days('Days carried forward').default(15),
      /** Days over the carry-forward limit at the year end. */
      excess: z.enum(['LOST', 'PAID']).default('LOST'),
      /** Leave may go below zero by up to this many days (advance leave). */
      advanceDays: days('Advance days', 30).default(0),
    })
    .prefault({}),
  sick: z
    .object({
      fullDays: days('Full-pay days').default(15),
      halfDays: days('Half-pay days').default(30),
      unpaidDays: days('Unpaid days').default(45),
      probationMonths: z.coerce.number().int().min(0).max(12).default(3),
    })
    .prefault({}),
  maternity: z.object({ fullDays: days('Full-pay days').default(45), halfDays: days('Half-pay days').default(15) }).prefault({}),
  parental: z.object({ days: days('Days').default(5) }).prefault({}),
  /**
   * Sandwich rule (owner, 2026-11-03): a weekly off or public holiday with a day away on the nearest working day
   * before AND after it counts as away too. Paid leave both sides: paid leave (the type of the day before); unpaid
   * leave both sides: unpaid leave; an absence (or paid and unpaid leave) either side: absent. From `from` on.
   * The old setting (weekly offs / holidays inside a leave) is replaced by this one.
   */
  sandwich: z.object({ mode: z.enum(['OFF', 'BEFORE_AND_AFTER']).default('BEFORE_AND_AFTER'), from: isoDate.default(SANDWICH_FROM) }).prefault({}),
  /** Weekly offs / holidays worked, given as days off instead of overtime pay (chosen when approving overtime). */
  compOff: z
    .object({
      /** Worked at least this long: a full day; at least the half: half a day. */
      fullDayMinutes: z.coerce.number().int().min(60).max(720).default(420),
      halfDayMinutes: z.coerce.number().int().min(30).max(720).default(240),
      /** Days not taken within this many days of being earned lapse (0: never). */
      expiryDays: z.coerce.number().int().min(0).max(730).default(90),
    })
    .prefault({}),
});
export type LeaveRules = z.infer<typeof leaveRulesSchema>;

// ---------- End-of-service gratuity and final settlement ----------

/** UAE law by default (Decree-Law 33/2021 art. 51): 21 days' basic a year for the first 5 years, 30 after; none under a year; at most 2 years' wage. */
export const gratuityRulesSchema = z.object({
  /** Day's basic wage: monthly basic ÷ 30 (MOHRE calculator) or × 12 ÷ 365. */
  dayBasis: z.enum(['THIRTY', 'YEAR']).default('THIRTY'),
  firstYears: z.coerce.number().int().min(0).max(40).default(5),
  firstDays: z.coerce.number().min(0).max(60).default(21),
  laterDays: z.coerce.number().min(0).max(60).default(30),
  /** No gratuity under this much service (years). */
  minYears: z.coerce.number().min(0).max(5).default(1),
  /** Total at most this many years' basic wage (0: no cap). */
  capYears: z.coerce.number().min(0).max(10).default(2),
  /** Unpaid leave days are not counted as service (UAE law). */
  excludeUnpaid: z.boolean().default(true),
  /** Unused annual leave paid out at the end: basic (UAE law) or gross wage a day. */
  leaveEncashment: z.enum(['BASIC', 'GROSS']).default('BASIC'),
  /** Monthly provision booked with payroll, from this month on (null: off). */
  provisionFrom: z.string().regex(/^\d{4}-\d{2}$/).nullable().default(null),
  /** The provision also accrues in the first year (the gratuity is owed once the year is complete). */
  accrueFirstYear: z.boolean().default(true),
});
export type GratuityRules = z.infer<typeof gratuityRulesSchema>;

/** Gratuity for a service length on a basic wage, with the working shown. */
export function gratuityFor(basic: number, serviceDays: number, rules: GratuityRules, accrual = false) {
  const years = Math.max(0, serviceDays) / 365;
  const day = rules.dayBasis === 'THIRTY' ? basic / 30 : (basic * 12) / 365;
  const firstYrs = Math.min(years, rules.firstYears);
  const laterYrs = Math.max(0, years - rules.firstYears);
  const first = firstYrs * rules.firstDays * day;
  const later = laterYrs * rules.laterDays * day;
  const cap = rules.capYears ? basic * 12 * rules.capYears : Infinity;
  const raw = first + later;
  const eligible = years >= rules.minYears;
  const amount = eligible || (accrual && rules.accrueFirstYear) ? Math.min(raw, cap) : 0;
  const r2 = (x: number) => Math.round(x * 100) / 100;
  return { years: Math.round(years * 1000) / 1000, day: r2(day), first: r2(first), later: r2(later), capped: raw > cap, eligible, amount: r2(amount) };
}

export const SETTLEMENT_REASONS = ['RESIGNATION', 'TERMINATION', 'END_OF_CONTRACT', 'RETIREMENT', 'DEATH', 'OTHER'] as const;
export type SettlementReason = (typeof SETTLEMENT_REASONS)[number];
export const SETTLEMENT_REASON_LABELS: Record<SettlementReason, string> = { RESIGNATION: 'Resignation', TERMINATION: 'Termination by the company', END_OF_CONTRACT: 'End of contract', RETIREMENT: 'Retirement', DEATH: 'Death', OTHER: 'Other' };
const item = z.object({ name: req('Item', 100), amount: z.coerce.number({ error: 'Enter the amount' }).positive('Enter the amount').max(10_000_000) });
const handoverItem = z.object({
  item: req('Item', 100),
  returned: z.boolean().default(false),
  /** Deducted when not returned. */
  value: z.coerce.number().min(0, 'Cannot be negative').max(1_000_000).default(0),
});
export type HandoverItem = z.infer<typeof handoverItem>;
/** The usual things to hand back, offered on a new settlement. */
export const HANDOVER_DEFAULTS = ['Tools', 'Uniform', 'ID / access card', 'Phone / SIM', 'Keys', 'Cash in hand / petty cash'];
const finalSettlementBase = z.object({
  employeeId: uuid('Choose the employee'),
  docDate: isoDate,
  lastDay: isoDate,
  reason: z.enum(SETTLEMENT_REASONS),
  /** Notice pay, air ticket, bonus … */
  earnings: z.array(item).max(20).default([]),
  /** Notice not served, damage … */
  deductions: z.array(item).max(20).default([]),
  notes: z.string().trim().max(1000).default(''),
  /** When notice was given (resignation or termination); blank when none (death, end of contract …). */
  noticeDate: z.preprocess((v) => (v === '' ? null : v), isoDate.nullable()).default(null),
  /** What the employee hands back: returned, or its value is deducted. */
  handover: z.array(handoverItem).max(30).default([]),
});
const noticeOk = (v: { noticeDate: string | null; lastDay: string }) => !v.noticeDate || v.noticeDate <= v.lastDay;
const noticeMsg = { path: ['noticeDate'], message: 'Notice is given on or before the last working day', ...always };
export const finalSettlementSchema = finalSettlementBase.refine(noticeOk, noticeMsg);
export const finalSettlementUpdateSchema = finalSettlementBase.extend({ version: z.number().int() }).refine(noticeOk, noticeMsg);
export type FinalSettlementInput = z.infer<typeof finalSettlementSchema>;

/** What a settlement pays and recovers, with the working. */
export interface SettlementFigures {
  hireDate: string | null;
  lastDay: string;
  serviceDays: number;
  unpaidDays: number;
  basic: number;
  gross: number;
  gratuity: ReturnType<typeof gratuityFor>;
  /** Gratuity provided for so far (opening + payroll provisions); the posting uses it first. */
  provided: number;
  leave: { days: number; dayRate: number; amount: number };
  earnings: { name: string; amount: number }[];
  deductions: { name: string; amount: number }[];
  loans: { id: string; number: string | null; kind: 'ADVANCE' | 'LOAN'; balance: number; recovered: number }[];
  /** Advances / loans still owed after the settlement (net pay cannot go below 0). */
  loansLeft: number;
  /** Notice: given on, days until the last day, the usual minimum (14 days in probation, else 30) and how short. */
  notice: { given: string | null; days: number | null; required: number; short: number; inProbation: boolean };
  /** Hand-over items neither returned nor valued: the settlement cannot be submitted or posted. */
  handoverOpen: string[];
  net: number;
  notes: string[];
}
export interface FinalSettlementDetail {
  id: string;
  number: string | null;
  docDate: string;
  status: string;
  employee: { id: string; empNo: string; name: string; department: string; designation: string };
  lastDay: string;
  reason: SettlementReason;
  earnings: { name: string; amount: number }[];
  deductions: { name: string; amount: number }[];
  noticeDate: string | null;
  handover: HandoverItem[];
  notes: string;
  figures: SettlementFigures;
  paymentVoucher: { id: string; number: string | null } | null;
  version: number;
  actions: string[];
}

/** One row of the opening balances upload: the days, or the date they came back from their last annual leave. */
export const leaveOpeningRowSchema = z.object({
  empNo: z.string().trim().min(1),
  days: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(-60).max(400).nullable()).default(null),
  lastReturn: z.preprocess((v) => (v === '' || v == null ? null : v), isoDate.refine((d) => d <= todayUae(), 'The return date cannot be in the future').nullable()).default(null),
  takenThisYear: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(366).nullable()).default(null),
  note: z.string().trim().max(300).default(''),
});
export type LeaveOpeningRow = z.infer<typeof leaveOpeningRowSchema>;
export const leaveOpeningsSchema = z.object({ asOf: isoDate, rows: z.array(leaveOpeningRowSchema).min(1).max(2000), commit: z.boolean().default(false) });
export const leaveOpeningSchema = z.object({ employeeId: uuid('Choose the employee'), asOf: isoDate, days: z.coerce.number().min(-60).max(400), note: z.string().trim().max(300).default('') });

export interface LeaveBalance {
  employeeId: string;
  /** Where the count starts: the opening balance, or the joining date. */
  start: { date: string; days: number; from: 'OPENING' | 'JOINING' | 'NONE' };
  earned: number;
  taken: number;
  booked: number;
  lapsed: number;
  balance: number;
  /** Earned per year now (12 / 30 …). */
  rate: number;
}

// ---------- Employee pay & attendance settings ----------

export const employeePaySettingsSchema = z.object({
  workScheduleId: uuid('Choose the work schedule'),
  overtimeEligible: z.boolean(),
  lateDeduction: z.boolean(),
  /** Attendance not tracked (e.g. managers): every working day counts as present. Replaces the old hidden auto-present list. */
  attendanceExempt: z.boolean(),
  version: z.number().int(),
});
export type EmployeePaySettingsInput = z.infer<typeof employeePaySettingsSchema>;

export const salaryRevisionSchema = z.object({
  effectiveFrom: isoDate,
  basic: positive('Basic salary'),
  housing: money('Housing allowance'),
  transport: money('Transport allowance'),
  other: money('Other allowance'),
  reason: req('Reason', 200),
});
export type SalaryRevisionInput = z.infer<typeof salaryRevisionSchema>;

// ---------- Leave request ----------

export const leaveRequestSchema = z
  .object({
    employeeId: uuid('Choose the employee'),
    leaveTypeId: uuid('Choose the leave type'),
    docDate: isoDate,
    fromDate: isoDate,
    toDate: isoDate,
    halfDay: z.boolean().default(false),
    reason: req('Reason', 500),
  })
  .refine((v) => v.toDate >= v.fromDate, { path: ['toDate'], message: 'The last day cannot be before the first day', ...always })
  .refine((v) => !v.halfDay || v.fromDate === v.toDate, { path: ['halfDay'], message: 'A half day is a single day', ...always });
export type LeaveRequestInput = z.infer<typeof leaveRequestSchema>;

// ---------- Attendance ----------

export const WORK_DAY_STATUSES = ['PRESENT', 'UNDER_HOURS', 'ABSENT', 'PAID_LEAVE', 'UNPAID_LEAVE', 'HOLIDAY', 'WEEKLY_OFF', 'NOT_EMPLOYED', 'EXEMPT'] as const;
export type WorkDayStatus = (typeof WORK_DAY_STATUSES)[number];
export const WORK_DAY_LABELS: Record<WorkDayStatus, { code: string; label: string }> = {
  PRESENT: { code: 'P', label: 'Present' },
  UNDER_HOURS: { code: 'U', label: 'Under minimum hours' },
  ABSENT: { code: 'A', label: 'Absent' },
  PAID_LEAVE: { code: 'L', label: 'Paid leave' },
  UNPAID_LEAVE: { code: 'UL', label: 'Unpaid leave' },
  HOLIDAY: { code: 'H', label: 'Public holiday' },
  WEEKLY_OFF: { code: 'W', label: 'Weekly off' },
  NOT_EMPLOYED: { code: '–', label: 'Not employed' },
  EXEMPT: { code: 'X', label: 'Exempt (paid)' },
};

export const manualDaySchema = z
  .object({
    employeeId: z.string().uuid(),
    date: isoDate,
    timeIn: z.union([z.literal(''), time]).nullable().optional().transform((v) => v || null),
    timeOut: z.union([z.literal(''), time]).nullable().optional().transform((v) => v || null),
    absent: z.boolean().default(false),
    note: req('Reason for the correction', 300),
  })
  .refine((v) => v.absent || !!v.timeIn, { path: ['timeIn'], message: 'Enter the time in (or mark absent)', ...always })
  .refine((v) => v.date <= todayUae(), { path: ['date'], message: 'Attendance cannot be entered for a day that has not come yet', ...always });
export type ManualDayInput = z.infer<typeof manualDaySchema>;

export interface WorkDayItem {
  date: string;
  status: WorkDayStatus;
  timeIn: string | null;
  timeOut: string | null;
  workedMinutes: number;
  lateMinutes: number;
  earlyMinutes: number;
  otMinutes: number;
  manual: boolean;
  note: string;
  /** A weekly off / holiday counted as absent or leave by the sandwich rule. */
  sandwich: boolean;
}

export interface AttendanceSheet {
  month: string;
  days: string[];
  rows: { employeeId: string; empNo: string; name: string; department: string; departmentId: string; days: WorkDayItem[]; totals: Record<string, number> }[];
}

// ---------- Earnings / deductions ----------

export const ADJUSTMENT_KINDS = ['EARNING', 'DEDUCTION'] as const;
export const ADJUSTMENT_CODES = ['ARREARS', 'BONUS', 'ALLOWANCE', 'COMMISSION', 'FINE', 'DAMAGE', 'OTHER'] as const;
export const ADJUSTMENT_CODE_LABELS: Record<(typeof ADJUSTMENT_CODES)[number], string> = {
  ARREARS: 'Arrears',
  BONUS: 'Bonus',
  ALLOWANCE: 'One-time allowance',
  COMMISSION: 'Commission',
  FINE: 'Fine',
  DAMAGE: 'Damage recovery',
  OTHER: 'Other',
};
export const payrollAdjustmentSchema = z
  .object({
    employeeId: uuid('Choose the employee'),
    month,
    kind: z.enum(ADJUSTMENT_KINDS),
    code: z.enum(ADJUSTMENT_CODES),
    amount: positive(),
    notes: opt(300),
    /** One-month allowance: its type. */
    allowanceTypeId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid('Choose the allowance').nullable()).default(null),
    /** Damage recovery: the account the damage was booked to. */
    accountId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid('Choose the account').nullable()).default(null),
  })
  .refine((v) => v.code !== 'DAMAGE' || v.kind !== 'DEDUCTION' || !!v.accountId, { path: ['accountId'], message: 'Choose the account the damage was booked to', ...always })
  .refine((v) => v.code !== 'FINE' || !!v.notes.trim(), { path: ['notes'], message: 'Say what the fine is for', ...always })
  .refine((v) => v.code !== 'ALLOWANCE' || v.kind !== 'EARNING' || !!v.allowanceTypeId, { path: ['allowanceTypeId'], message: 'Choose the allowance', ...always });
export type PayrollAdjustmentInput = z.infer<typeof payrollAdjustmentSchema>;

// ---------- Advances & loans ----------

export const LOAN_KINDS = ['ADVANCE', 'LOAN'] as const;
export const loanSchema = z
  .object({
    employeeId: uuid('Choose the employee'),
    kind: z.enum(LOAN_KINDS),
    docDate: isoDate,
    amount: positive(),
    /** Recovered from each payroll (advances: usually the whole amount in the next payroll). */
    installment: positive('Monthly recovery'),
    startMonth: month,
    /** Bank / cash account the money is paid from. */
    cashAccountId: uuid('Choose the bank or cash account'),
    reason: req('Reason', 300),
  })
  .refine((v) => v.installment <= v.amount, { path: ['installment'], message: 'Cannot exceed the amount', ...always })
  .refine((v) => v.startMonth >= v.docDate.slice(0, 7), { path: ['startMonth'], message: 'Recovery cannot start before the loan date', ...always });
export type LoanInput = z.infer<typeof loanSchema>;

/** One month's recovery changed by HR: 0 skips the month (the balance moves on). */
export const recoveryChangeSchema = z.object({ month, amount: money('Recovery'), reason: req('Reason', 300) });
export type RecoveryChangeInput = z.infer<typeof recoveryChangeSchema>;
/** Reschedule: a new monthly recovery from a month onwards. */
export const rescheduleLoanSchema = z.object({ installment: positive('Monthly recovery'), reason: req('Reason', 300) });
export type RescheduleLoanInput = z.infer<typeof rescheduleLoanSchema>;

export interface LoanItem {
  id: string;
  number: string | null;
  kind: (typeof LOAN_KINDS)[number];
  employee: { id: string; empNo: string; name: string };
  docDate: string;
  amount: number;
  installment: number;
  startMonth: string;
  recovered: number;
  balance: number;
  status: import('./documents').DocStatus;
  migrated: boolean;
}

// ---------- Payroll run ----------

export const payrollRunSchema = z.object({
  month,
  docDate: isoDate,
  notes: opt(500),
});
export type PayrollRunInput = z.infer<typeof payrollRunSchema>;

export interface PayrollLineItem {
  id: string;
  employee: { id: string; empNo: string; name: string };
  department: string;
  departmentId: string | null;
  designation: string;
  /** Designation rank (seniority; 0 = not ranked). */
  rank: number;
  /** Basic salary and allowances (housing, transport, other) of the salary in force at the month end. */
  basic: number;
  allowance: number;
  /** The monthly allowances by name (salary in force at the month end). */
  allowances: { name: string; amount: number }[];
  /** Values changed by hand on the salary sheet (recoveries changed; arrears / deductions added there). */
  overrides: PayrollOverride[];
  /** One-off earnings / deductions of this run by name (one-month allowances by their type, arrears …). */
  earningItems: { name: string; amount: number; notes: string }[];
  deductionItems: { name: string; amount: number; notes: string }[];
  costCentre: string;
  monthlySalary: number;
  daysInMonth: number;
  paidDays: number;
  presentDays: number;
  paidLeaveDays: number;
  holidays: number;
  weeklyOffs: number;
  absentDays: number;
  lateMinutes: number;
  otMinutes: number;
  earned: number;
  lateDeduction: number;
  earlyMinutes: number;
  earlyDeduction: number;
  /** Gratuity provision booked with this line (not part of pay). */
  gratuity?: number;
  otAmount: number;
  earnings: number;
  deductions: number;
  /** Deductions that did not fit this month (net pay too low, or the 50% cap), moved to next month. */
  carriedForward: number;
  advanceRecovery: number;
  loanRecovery: number;
  net: number;
  notes: string[];
  /** How this line is paid; null = not set on the employee. */
  payMethod: 'BANK' | 'CASH' | 'CHEQUE' | null;
  held: boolean;
  holdReason: string;
  /** The payment voucher that paid it (null: not paid yet). */
  payment: { id: string; number: string | null } | null;
}

/** A value changed by hand on the salary sheet: shown in red with who, when and why. */
export interface PayrollOverride {
  field: 'adv' | 'loan' | 'arrear' | 'ded';
  /** What it would have been, and what it is. */
  normal: number;
  value: number;
  reason: string;
  by: string | null;
  at: string;
  /** The advance / loan (recoveries) or the earning / deduction it is. */
  refId: string;
  label: string;
}

export interface PayrollRunDetail {
  id: string;
  number: string | null;
  month: string;
  /** The month is not over: figures count the days still to come as worked; it can be submitted after the month end. */
  provisional: boolean;
  /** Temporary advances given up to this date are recovered in this run. */
  advanceCutoff: string;
  /** How many values were changed by hand (and by whom, so they cannot approve it). */
  overrideCount: number;
  overriddenBy: string[];
  docDate: string;
  status: import('./documents').DocStatus;
  notes: string;
  calculatedAt: string | null;
  lines: PayrollLineItem[];
  totals: { employees: number; earned: number; otAmount: number; lateDeduction: number; earlyDeduction: number; earnings: number; deductions: number; carriedForward: number; recoveries: number; net: number; gratuity?: number };
  paymentVoucher: { id: string; number: string | null } | null;
  migrated: boolean;
  /** Accounts still to set before the run can be submitted, approved or posted (it can be calculated and checked). */
  accountGaps: { code: string; text: string; departments?: { id: string; name: string; employees: number }[] }[];
  version: number;
  actions: import('./documents').WorkflowAction[];
}

// ---------- Attendance policies and shift assignments (HR step 2) ----------

/**
 * Policy types. LATE_EARLY (one policy for late coming and early leaving) is what is created; on a day it supplies
 * the LATE and EARLY_LEAVE rules the judge and payroll read. Stored LATE / EARLY_LEAVE policies were converted to it.
 */
export const ATTENDANCE_POLICY_TYPES = ['OVERTIME', 'LATE_EARLY', 'LATE', 'EARLY_LEAVE', 'MIN_HOURS', 'BREAK', 'TIME'] as const;
export type AttendancePolicyType = (typeof ATTENDANCE_POLICY_TYPES)[number];
/** The types a new policy can be (in the order shown). */
export const CREATABLE_POLICY_TYPES = ['TIME', 'LATE_EARLY', 'MIN_HOURS', 'BREAK', 'OVERTIME'] as const satisfies readonly AttendancePolicyType[];
export const ATTENDANCE_POLICY_LABELS: Record<AttendancePolicyType, string> = {
  OVERTIME: 'Overtime', LATE_EARLY: 'Late & early leave', LATE: 'Late deduction', EARLY_LEAVE: 'Early leave', MIN_HOURS: 'Minimum working hours', BREAK: 'Break', TIME: 'Time',
};
/** Number prefix per type: POL-OT-0001. */
export const ATTENDANCE_POLICY_PREFIX: Record<AttendancePolicyType, string> = { OVERTIME: 'OT', LATE_EARLY: 'LATE', LATE: 'LATE', EARLY_LEAVE: 'EARLY', MIN_HOURS: 'MINH', BREAK: 'BRK', TIME: 'TIME' };

export const OT_GIVEN_AS = ['PAY', 'COMP_OFF'] as const;
const mins = (label: string, max = 1440) => z.coerce.number({ error: `Enter ${label.toLowerCase()}` }).int(`${label} must be whole minutes`).min(0, `${label} cannot be negative`).max(max, `${label} is at most ${max}`);
const rate = (label: string) => z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(10, `${label} is at most 10`);

export const overtimeSettingsSchema = z.object({
  base: z.enum(['BASIC', 'GROSS']).default('BASIC'),
  normalRate: rate('Normal rate').default(1.25),
  nightRate: rate('Night rate').default(1.5),
  nightFrom: time.default('22:00'),
  nightTo: time.default('04:00'),
  offDayRate: rate('Off-day rate').default(1.5),
  holidayRate: rate('Holiday rate').default(1.5),
  startAfterMinutes: mins('Start after').default(0),
  minimumMinutes: mins('Minimum overtime').default(0),
  dailyCapMinutes: mins('Daily cap').default(120),
  weeklyCapHours: mins('Weekly cap', 168).default(0),
  monthlyCapHours: mins('Monthly cap', 744).default(0),
  earlyArrivalCounts: z.boolean().default(false),
  /** Work on a weekly off is overtime; `holidayCounts` the same for public holidays (unset: follows the weekly off). */
  offDayCounts: z.boolean().default(false),
  holidayCounts: z.boolean().optional(),
  /** Off-day / holiday overtime given as pay, or later as comp-off days (with the leave balances). */
  offDayGivenAs: z.enum(OT_GIVEN_AS).default('PAY'),
  holidayGivenAs: z.enum(OT_GIVEN_AS).default('PAY'),
  needsApproval: z.boolean().default(false),
});
export const lateSettingsSchema = z.object({
  graceMinutes: mins('Grace').default(0),
  freePerMonth: mins('Free times a month', 31).default(0),
  mode: z.enum(LATE_MODES).default('PER_MINUTE'),
  rate: rate('Rate').default(1),
  count: z.coerce.number().int().min(1, 'At least 1').max(31).default(3),
});
export const earlyLeaveSettingsSchema = lateSettingsSchema;
/** One policy for both: each part can be switched off (no deduction for it). */
export const lateEarlySettingsSchema = z.object({
  late: lateSettingsSchema.extend({ enabled: z.boolean().default(true) }).prefault({}),
  early: earlyLeaveSettingsSchema.extend({ enabled: z.boolean().default(true) }).prefault({}),
});
export const minHoursSettingsSchema = z.object({
  minimumMinutes: mins('Minimum working time').default(0),
  below: z.enum(['HALF_DAY', 'ABSENT', 'DEDUCT_SHORTFALL']).default('ABSENT'),
  halfDayFromMinutes: mins('Half day from').default(0),
});
export const breakSettingsSchema = z.object({
  breaks: z.array(breakSchema).max(6).default([]),
  untimedUnpaidMinutes: mins('Untimed break').default(0),
  untimedAfterMinutes: mins('Untimed break after').default(0),
});
export const timeSettingsSchema = z.object({
  doubleTapMinutes: mins('Double tap', 60).default(2),
  windowBeforeShiftMinutes: mins('Window before shift').default(240),
  nightCarryMinutes: mins('Night carry').default(240),
  missingOut: z.enum(['NOT_COUNTED', 'SHIFT_END']).default('NOT_COUNTED'),
});
export const ATTENDANCE_POLICY_SETTINGS = {
  OVERTIME: overtimeSettingsSchema, LATE_EARLY: lateEarlySettingsSchema, LATE: lateSettingsSchema, EARLY_LEAVE: earlyLeaveSettingsSchema,
  MIN_HOURS: minHoursSettingsSchema, BREAK: breakSettingsSchema, TIME: timeSettingsSchema,
} as const;
export type AttendancePolicySettings = { [K in AttendancePolicyType]: z.infer<(typeof ATTENDANCE_POLICY_SETTINGS)[K]> };

export const HR_TARGET_KINDS = ['ALL', 'BRANCH', 'DEPARTMENT', 'DESIGNATION', 'SHIFT', 'EMPLOYEE'] as const;
export type HrTargetKind = (typeof HR_TARGET_KINDS)[number];
export const HR_TARGET_LABELS: Record<HrTargetKind, string> = { ALL: 'Everyone', BRANCH: 'Branch', DEPARTMENT: 'Department', DESIGNATION: 'Designation', SHIFT: 'Shift', EMPLOYEE: 'Employee' };
export const targetSchema = z
  .object({ kind: z.enum(HR_TARGET_KINDS), refId: z.string().uuid().nullable().default(null) })
  .refine((t) => (t.kind === 'ALL') === (t.refId == null), { path: ['refId'], message: 'Choose who this applies to', ...always });
export type TargetInput = z.infer<typeof targetSchema>;
const targetsSchema = (shifts: boolean) =>
  z
    .object({ include: z.array(targetSchema).max(500), exclude: z.array(targetSchema).max(500).default([]) })
    .refine((t) => t.include.length > 0, { path: ['include'], message: 'Choose who this applies to', ...always })
    .refine((t) => shifts || [...t.include, ...t.exclude].every((x) => x.kind !== 'SHIFT'), { path: ['include'], message: 'A shift cannot be chosen here', ...always })
    .refine((t) => t.exclude.every((x) => x.kind !== 'ALL'), { path: ['exclude'], message: 'Everyone cannot be excluded', ...always });

const dated = <T extends { effectiveFrom?: string; effectiveTo?: string | null; fromDate?: string; toDate?: string | null }>(v: T) => {
  const from = v.effectiveFrom ?? v.fromDate, to = v.effectiveTo ?? v.toDate;
  return !to || !from || to >= from;
};

export const attendancePolicySchema = z
  .object({
    type: z.enum(ATTENDANCE_POLICY_TYPES),
    settings: z.record(z.string(), z.unknown()),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable().optional().default(null),
    notes: opt(500),
    targets: targetsSchema(true),
  })
  .refine(dated, { path: ['effectiveTo'], message: 'End date must be on or after the start', ...always })
  .transform((v, ctx) => {
    const r = ATTENDANCE_POLICY_SETTINGS[v.type].safeParse(v.settings);
    if (!r.success) {
      for (const i of r.error.issues) ctx.addIssue({ ...i, path: ['settings', ...i.path] } as never);
      return z.NEVER;
    }
    return { ...v, settings: r.data };
  });
export type AttendancePolicyInput = z.infer<typeof attendancePolicySchema>;

export const shiftAssignmentSchema = z
  .object({
    workScheduleId: uuid('Choose the shift'),
    fromDate: isoDate,
    toDate: isoDate.nullable().optional().default(null),
    notes: opt(500),
    targets: targetsSchema(false),
  })
  .refine(dated, { path: ['toDate'], message: 'End date must be on or after the start', ...always });
export type ShiftAssignmentInput = z.infer<typeof shiftAssignmentSchema>;

export interface HrTargetView { kind: HrTargetKind; refId: string | null; exclude: boolean; label: string }
export interface AttendancePolicyListItem {
  id: string; number: string; type: AttendancePolicyType; title: string; effectiveFrom: string; effectiveTo: string | null;
  isActive: boolean; appliesTo: string; people: number;
}
export interface AttendancePolicyDetail extends AttendancePolicyListItem {
  settings: Record<string, unknown>; notes: string; version: number; targets: HrTargetView[];
  /** Payroll was submitted with this policy up to this date (changes then start after it, as a new policy). */
  paidThrough: string | null;
}
export interface ShiftAssignmentView {
  id: string; workScheduleId: string; shift: string; fromDate: string; toDate: string | null; notes: string;
  isActive: boolean; version: number; targets: HrTargetView[]; people: number;
}
/** What saving would change: who is covered, who moves from another policy / shift, and ties that block the save. */
export interface CoveragePreview {
  people: number;
  moving: { employeeId: string; name: string; from: string }[];
  conflicts: { employeeId: string; name: string; other: string; otherId: string }[];
}

// ---------- Attendance machines ----------

export const deviceSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(60),
  host: z.string().trim().min(3, 'Enter the IP address or host name').max(120),
  port: z.coerce.number().int().min(1).max(65535).default(4370),
  /** Blank keeps the stored key; 0 means none. */
  commKey: z.union([z.literal(''), z.coerce.number().int().min(0).max(999999)]).optional(),
  branchId: z.string().uuid().nullable().optional().default(null),
  pollMinutes: z.coerce.number().int().min(1, 'At least every minute').max(1440).default(5),
  isActive: z.boolean().default(true),
});
export type DeviceInput = z.infer<typeof deviceSchema>;
/** Which employee a machine User ID belongs to, from a date. */
export const mappingSchema = z.object({
  deviceUserId: z.string().trim().min(1, 'Enter the User ID on the machine').max(24),
  employeeId: z.string().uuid('Choose the employee'),
  deviceId: z.string().uuid().nullable().optional().default(null),
  fromDate: isoDate,
  toDate: isoDate.nullable().optional().default(null),
});
export type MappingInput = z.infer<typeof mappingSchema>;

// ---------- Department hierarchy ----------

export interface DeptLike { id: string; name: string; parentId?: string | null; sortOrder?: number | null }
export type DeptInTree<T extends DeptLike> = T & { depth: number; /** Sorts in tree order. */ path: string; /** Ids from the top down to this one. */ chain: string[] };

/**
 * Departments in hierarchy order: each parent followed by its children (by order, then name), with depth and a sort
 * key. A department whose parent is missing (or in a loop) is placed at the top.
 */
export function departmentTree<T extends DeptLike>(list: T[]): DeptInTree<T>[] {
  const ids = new Set(list.map((d) => d.id));
  const kids = new Map<string | null, T[]>();
  for (const d of list) {
    const p = d.parentId && ids.has(d.parentId) && d.parentId !== d.id ? d.parentId : null;
    kids.set(p, [...(kids.get(p) ?? []), d]);
  }
  for (const k of kids.values()) k.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
  const out: DeptInTree<T>[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number, path: string, chain: string[]) => {
    (kids.get(parent) ?? []).forEach((d, i) => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      const p = `${path}${String(i).padStart(4, '0')}`;
      out.push({ ...d, depth, path: p, chain: [...chain, d.id] });
      walk(d.id, depth + 1, `${p}.`, [...chain, d.id]);
    });
  };
  walk(null, 0, '', []);
  // Anything left (a loop) goes at the end, at the top level.
  for (const d of list) if (!seen.has(d.id)) out.push({ ...d, depth: 0, path: `9999${d.name}`, chain: [d.id] });
  return out;
}

/** A department and everything under it. */
export function departmentAndBelow(list: DeptLike[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const d of list) if (d.parentId && out.has(d.parentId) && !out.has(d.id)) (out.add(d.id), (grew = true));
  }
  return out;
}

export const departmentTreeSchema = z.object({
  rows: z.array(z.object({ id: z.string().uuid(), parentId: z.string().uuid().nullable(), sortOrder: z.coerce.number().int().min(0).max(100000) })).min(1).max(1000),
});

// ---------- Allowances and salary changes ----------

export const ALLOWANCE_SLOTS = ['HOUSING', 'TRANSPORT', 'OTHER'] as const;
export const allowanceTypeSchema = z.object({
  code: z.string().trim().min(1).max(20).regex(/^[A-Z0-9_-]+$/, 'Use uppercase letters, numbers, dash'),
  name: req('Name', 80),
  followsAttendance: z.boolean().default(true),
  inOtBase: z.boolean().default(true),
  wpsFixed: z.boolean().default(true),
  slot: z.enum(ALLOWANCE_SLOTS).default('OTHER'),
  accountId: z.union([z.literal(''), z.string().uuid()]).nullable().optional().transform((v) => v || null),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
  isActive: z.boolean(),
});
export type AllowanceTypeInput = z.infer<typeof allowanceTypeSchema>;

export const SALARY_CHANGE_KINDS = ['START', 'INCREMENT', 'ALLOWANCE_ADD', 'ALLOWANCE_CHANGE', 'ALLOWANCE_STOP', 'REVISION'] as const;
export type SalaryChangeKind = (typeof SALARY_CHANGE_KINDS)[number];
export const SALARY_CHANGE_LABELS: Record<SalaryChangeKind, string> = { START: 'Starting salary', INCREMENT: 'Increment', ALLOWANCE_ADD: 'Allowance added', ALLOWANCE_CHANGE: 'Allowance changed', ALLOWANCE_STOP: 'Allowance stopped', REVISION: 'Salary revision' };
const amount = (label: string) => z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(10_000_000);

/** A change of pay from a date: a new basic, and / or allowance amounts by type (0 stops it). */
export const salaryChangeSchema = z
  .object({
    employeeId: uuid('Choose the employee'),
    kind: z.enum(SALARY_CHANGE_KINDS),
    effectiveFrom: isoDate,
    basic: z.preprocess((v) => (v === '' || v == null ? null : v), amount('Basic salary').nullable()).default(null),
    allowances: z.array(z.object({ typeId: uuid('Choose the allowance'), amount: amount('Amount') })).max(20).default([]),
    reason: req('Reason', 300),
  })
  .refine((v) => (v.kind !== 'INCREMENT' && v.kind !== 'START') || v.basic != null, { path: ['basic'], message: 'Enter the new basic salary', ...always })
  .refine((v) => v.kind === 'INCREMENT' || v.kind === 'START' || v.allowances.length > 0 || (v.kind === 'REVISION' && v.basic != null), { path: ['allowances'], message: 'Choose the allowance', ...always })
  .refine((v) => new Set(v.allowances.map((a) => a.typeId)).size === v.allowances.length, { path: ['allowances'], message: 'Each allowance once', ...always });
export type SalaryChangeInput = z.infer<typeof salaryChangeSchema>;

/** The salary entered with a new employee: basic and the usual running allowances, from the hire date. */
export const startingSalarySchema = z.object({
  basic: z.coerce.number({ error: 'Enter the basic salary' }).positive('Enter the basic salary').max(10_000_000),
  housing: amount('Housing').default(0),
  transport: amount('Transport').default(0),
  other: amount('Other allowance').default(0),
});
export type StartingSalaryInput = z.infer<typeof startingSalarySchema>;

/** Who a bulk pay change is for: named people and groups, less exceptions. */
const salaryWho = {
  employeeIds: z.array(uuid('Employee')).max(2000).default([]),
  departmentIds: z.array(uuid('Department')).max(200).default([]),
  designationIds: z.array(uuid('Designation')).max(200).default([]),
  workScheduleIds: z.array(uuid('Shift')).max(50).default([]),
  exceptIds: z.array(uuid('Employee')).max(2000).default([]),
};
/** Who a bulk change leaves out unless ticked back in (each shown with the reason). */
const salarySkips = z
  .object({
    probation: z.boolean().default(true),
    /** Joined less than this many months before the date (0: off). */
    joinedMonths: z.coerce.number().int().min(0).max(60).default(6),
    /** Had an increment within this many months before the date (0: off). */
    incrementMonths: z.coerce.number().int().min(0).max(60).default(12),
  })
  .prefault({});
export const SALARY_ROUNDINGS = [0, 1, 5, 10] as const;

/**
 * Bulk increment (on basic: a % of the current basic, or a fixed amount) or bulk allowance (add / change / stop one
 * running allowance: a fixed amount, a % of basic, or a raise of the current amount by an amount or a %).
 * `commit = false` previews; `commit = true` sends one batch for approval.
 */
export const salaryBulkSchema = z
  .object({
    ...salaryWho,
    action: z.enum(['INCREMENT', 'ALLOWANCE']),
    mode: z.enum(['PERCENT', 'AMOUNT']).default('PERCENT'),
    value: z.coerce.number().min(0).max(10_000_000).default(0),
    rounding: z.coerce.number().refine((v) => (SALARY_ROUNDINGS as readonly number[]).includes(v), 'Choose the rounding').default(5),
    typeId: uuid('Allowance').nullable().default(null),
    allowanceAction: z.enum(['ADD', 'CHANGE', 'STOP']).default('ADD'),
    allowanceMode: z.enum(['FIXED', 'PERCENT_OF_BASIC', 'RAISE_AMOUNT', 'RAISE_PERCENT']).default('FIXED'),
    effectiveFrom: isoDate,
    reason: req('Reason', 300),
    skip: salarySkips,
    /** Skipped people ticked back in. */
    include: z.array(uuid('Employee')).max(2000).default([]),
    commit: z.boolean().default(false),
  })
  .refine((v) => v.employeeIds.length + v.departmentIds.length + v.designationIds.length + v.workScheduleIds.length > 0, { path: ['employeeIds'], message: 'Choose who', ...always })
  .refine((v) => !(v.action === 'INCREMENT' || v.allowanceAction !== 'STOP') || v.value > 0, { path: ['value'], message: 'Enter the amount or %', ...always })
  .refine((v) => v.action === 'INCREMENT' || !!v.typeId, { path: ['typeId'], message: 'Choose the allowance', ...always });
export type SalaryBulkInput = z.infer<typeof salaryBulkSchema>;

/** One row of an Excel / CSV salary upload: the new basic and / or allowance amounts (blank: unchanged; 0 stops). */
export const salaryImportSchema = z.object({
  effectiveFrom: isoDate,
  reason: req('Reason', 300),
  rows: z
    .array(z.object({ empNo: z.string().trim().min(1).max(30), basic: z.number().min(0).max(10_000_000).nullable().default(null), allowances: z.record(z.string(), z.number().min(0).max(10_000_000).nullable()).default({}) }))
    .min(1)
    .max(3000),
  commit: z.boolean().default(false),
});
export type SalaryImportInput = z.infer<typeof salaryImportSchema>;

/** The preview of a bulk pay change or an upload, person by person, with what it costs. */
export interface SalaryBulkPreview {
  rows: {
    employeeId: string;
    empNo: string;
    name: string;
    department: string;
    designation: string | null;
    /** Basic and the changed allowance (or gross) before and after. */
    before: { basic: number; part: number | null; gross: number };
    after: { basic: number; part: number | null; gross: number };
    /** Why left out (null: in). Ticking it in includes it anyway (except "cannot" reasons). */
    skipped: string | null;
    canInclude: boolean;
    /** Over the setting's %: a final approval by a second person is needed. */
    needsFinal: boolean;
  }[];
  ok: number;
  skipped: number;
  /** Extra gross a month and a year, by department; gratuity liability change (on basic, service to the date). */
  monthly: number;
  yearly: number;
  byDepartment: { department: string; people: number; monthly: number }[];
  gratuity: number;
  batchId: string | null;
}

/** Salaries & Allowances list: everyone's pay today, by allowance type, with what is waiting or still to come. */
export interface SalaryListRow {
  employeeId: string;
  empNo: string;
  name: string;
  department: string;
  departmentId: string;
  designation: string | null;
  photoAt: string | null;
  hireDate: string | null;
  basic: number;
  allowances: Record<string, number>;
  gross: number;
  since: string | null;
  lastIncrement: string | null;
  /** A change waiting for approval / an approved change from a later date. */
  waiting: { kind: SalaryChangeKind; from: string; gross: number } | null;
  upcoming: { from: string; gross: number } | null;
}

/** The same running allowance added, changed or stopped for many employees at once (each a request to approve). */
export const bulkSalaryChangeSchema = z.object({
  kind: z.enum(['ALLOWANCE_ADD', 'ALLOWANCE_CHANGE', 'ALLOWANCE_STOP']),
  typeId: uuid('Choose the allowance'),
  amount: z.coerce.number().min(0).max(10_000_000).default(0),
  effectiveFrom: isoDate,
  reason: req('Reason', 300),
  departmentIds: z.array(z.string().uuid()).max(200).default([]),
  employeeIds: z.array(z.string().uuid()).max(2000).default([]),
  commit: z.boolean().default(false),
});
export type BulkSalaryChangeInput = z.infer<typeof bulkSalaryChangeSchema>;

/** Attendance › Overtime: one employee's overtime for a month and its approval. */
/** UAE law: overtime at most 2 hours a day, save emergencies (Federal Decree-Law 33/2021, art. 19). */
export const OVERTIME_DAILY_LIMIT_MIN = 120;
export interface OvertimeRow {
  employeeId: string;
  empNo: string;
  name: string;
  department: string;
  departmentId: string;
  designation: string;
  /** Overtime worked this month (minutes), and the weekly offs / holidays worked. */
  otMinutes: number;
  offDays: { date: string; status: 'WEEKLY_OFF' | 'HOLIDAY'; workedMinutes: number; otMinutes: number; compOffDays: number }[];
  /** Days with more overtime than the daily limit (OVERTIME_DAILY_LIMIT_MIN): allowed only in an emergency. */
  overLimit: { date: string; otMinutes: number }[];
  approval: {
    id: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    otMinutes: number;
    approvedMinutes: number;
    compOffDates: string[];
    compOffDays: number;
    firstBy: string | null;
    firstById: string | null;
    firstAt: string | null;
    finalBy: string | null;
    finalAt: string | null;
    note: string;
    version: number;
  } | null;
  /** Worked overtime changed since it was decided (approve again). */
  changed: boolean;
}
export interface OvertimeMonth { month: string; levels: 'NONE' | 'ONE' | 'TWO'; locked: boolean; rows: OvertimeRow[] }
export const overtimeDecisionSchema = z.object({
  employeeId: uuid('Choose the employee'),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  /** Overtime approved for pay (minutes, not counting days given as comp-off). */
  approvedMinutes: z.coerce.number().int().min(0).max(744 * 60),
  compOffDates: z.array(isoDate).max(31).default([]),
  note: z.string().trim().max(300).default(''),
  version: z.number().int().nullable().default(null),
});
export type OvertimeDecisionInput = z.infer<typeof overtimeDecisionSchema>;

export interface PayStructure { effectiveFrom: string | null; basic: number; allowances: { typeId: string; name: string; amount: number; since: string | null }[]; gross: number }
/** Printed letter of an approved pay change: pay the day before and from the date. */
export interface SalaryChangeLetterData {
  id: string;
  reference: string;
  kind: SalaryChangeKind;
  title: string;
  effectiveFrom: string;
  dayBefore: string;
  employee: { empNo: string; name: string; designation: string; department: string };
  before: PayStructure;
  after: PayStructure;
  arrears: number | null;
  approvedBy: string | null;
  approvedAt: string | null;
}
export interface SalaryChangeView {
  id: string;
  employeeId: string;
  employee: string;
  kind: SalaryChangeKind;
  effectiveFrom: string;
  basic: number | null;
  allowances: { typeId: string; name: string; amount: number }[];
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedBy: string | null;
  requestedById: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string;
  arrears: number | null;
  /** Asked with others at once (approved together). */
  batchId: string | null;
  /** A large change: first approval given by … (null: not yet), then a final one by someone else. */
  needsFinal: boolean;
  firstBy: string | null;
  firstById: string | null;
  /** Gross before → after (worked out when asked; approval uses the pay in force then). */
  before: number;
  after: number;
  version: number;
}

// ---------- Air tickets (client, 31 Oct 2026) ----------
// One one-way ticket for each completed year of service, carried over while unused (a round trip uses two). The
// company books the ticket at the fare of the day, never cash instead; employees only, no family. Whoever leaves, for
// any reason, gets one one-way exit ticket when they leave the country within 30 days of the last day; the rest lapse.

export const AIR_TICKET_KINDS = ['ONE_WAY', 'ROUND_TRIP', 'EXIT'] as const;
export type AirTicketKind = (typeof AIR_TICKET_KINDS)[number];
export const AIR_TICKET_KIND_LABELS: Record<AirTicketKind, string> = { ONE_WAY: 'One-way', ROUND_TRIP: 'Round trip', EXIT: 'Exit ticket (one-way, on leaving)' };
/** Tickets of the balance a booking uses (the exit ticket is apart from it). */
export const AIR_TICKETS_USED: Record<AirTicketKind, number> = { ONE_WAY: 1, ROUND_TRIP: 2, EXIT: 0 };
/** The exit ticket is booked only for travel within this many days of the last day. */
export const EXIT_TICKET_DAYS = 30;

export const airTicketBookingSchema = z.object({
  employeeId: uuid('Choose the employee'),
  kind: z.enum(AIR_TICKET_KINDS, { error: 'Choose one-way, round trip or exit ticket' }),
  bookedOn: isoDate.refine((d) => d <= todayUae(), { message: 'The booking date cannot be in the future', ...always }),
  travelDate: isoDate,
  returnDate: isoDate.nullable().optional(),
  route: req('Route', 120),
  fare: positive('Fare'),
  /** The travel agent (a supplier); the fare is owed to them. */
  agentId: uuid('Choose the travel agent'),
  reference: opt(60),
  notes: opt(500),
});
export type AirTicketBookingInput = z.infer<typeof airTicketBookingSchema>;

export const airTicketCancelSchema = z.object({ reason: req('Reason', 300), version: z.number().int() });

/** Opening balances: unused tickets on a date (from the client's sheet). */
export const airTicketOpeningsSchema = z.object({
  asOf: isoDate.refine((d) => d <= todayUae(), { message: 'The date cannot be in the future', ...always }),
  rows: z.array(z.object({ empNo: req('Employee number', 30), tickets: z.coerce.number({ error: 'Enter the tickets' }).int('Whole tickets only').min(0, 'Cannot be negative').max(60), note: opt(200) })).min(1, 'Add at least one row').max(2000),
  commit: z.boolean().default(false),
});
export type AirTicketOpeningsInput = z.infer<typeof airTicketOpeningsSchema>;

export interface AirTicketBalance {
  employeeId: string;
  empNo: string;
  name: string;
  department: string;
  departmentId: string;
  status: string;
  hireDate: string | null;
  /** Service counted (unpaid leave left out when the gratuity rules say so). */
  serviceDays: number;
  years: number;
  /** From the opening sheet (null: none) and the years completed after it. */
  opening: { asOf: string; tickets: number } | null;
  earned: number;
  used: number;
  left: number;
  /** The date the next ticket is earned (later if unpaid leave is taken). */
  nextOn: string | null;
  lastFare: number | null;
  /** Owed, valued at the employee's last fare (else the average fare). */
  value: number;
  /** Has left: the last day and whether the exit ticket is still open / booked / lapsed. */
  exit: { lastDay: string; until: string; state: 'OPEN' | 'BOOKED' | 'LAPSED' } | null;
}

export interface AirTicketBookingView {
  id: string;
  employeeId: string;
  empNo: string;
  name: string;
  department: string;
  kind: AirTicketKind;
  tickets: number;
  bookedOn: string;
  travelDate: string;
  returnDate: string | null;
  route: string;
  fare: number;
  agent: { id: string; name: string } | null;
  reference: string;
  notes: string;
  status: 'BOOKED' | 'CANCELLED';
  cancelReason: string;
  journal: { id: string; number: string | null; status: string } | null;
  createdBy: string | null;
  createdAt: string;
  version: number;
}

export interface AirTicketSummary {
  asOf: string;
  rows: AirTicketBalance[];
  bookings: AirTicketBookingView[];
  totals: { earned: number; used: number; left: number; value: number; fares: number };
  averageFare: number | null;
}
